// EU-696: instala o polyfill de `Promise.withResolvers` ANTES do `react-pdf` — o
// `pdfjs-dist` 5.4.x chama o método CRU no caminho de carga do documento (main
// thread + worker), e em mobile antigo (Safari <17.4 / Android WebView) sem ele
// o `<Document>` lança e cai no fallback. Precisa ser o PRIMEIRO import (imports
// ES avaliam na ordem do código). No-op nos browsers que já têm o método.
import './promise-with-resolvers';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
// Camada de anotação do PDF.js (EU-491): posiciona os hyperlinks sobre o
// canvas. Sem este CSS os `<a>` renderizam empilhados/ocultos e o clique não
// chega ao link. Pareado com `renderAnnotationLayer` na `<Page>`.
import 'react-pdf/dist/Page/AnnotationLayer.css';
import { Button } from '../button';
import { cn } from '../../../lib/utils';

/**
 * Worker do PDF.js. Navegadores mobile (Chrome Android / Safari iOS) NÃO
 * renderizam PDF inline em `<iframe>`/`<embed>` — mostram só uma caixa com o
 * nome do arquivo + "Abrir". O PDF.js renderiza cada página em `<canvas>` no
 * próprio JS, então funciona igual em mobile e desktop. O Vite copia o worker
 * do `pdfjs-dist` que o `react-pdf` fixa como asset (mesma versão da API,
 * senão o worker recusa o documento). */
const PDF_WORKER_URL = new URL('pdfjs-dist/build/pdf.worker.min.mjs', import.meta.url).toString();
pdfjs.GlobalWorkerOptions.workerSrc = PDF_WORKER_URL;

/**
 * DEV-483 — o worker é um módulo ES (`.mjs`) e o browser só o aceita em
 * `new Worker(url, { type: 'module' })` se o servidor responder com MIME de
 * JavaScript. Um servidor estático que entrega `.mjs` como
 * `application/octet-stream` (o nginx do slot de QA, por exemplo) derruba o
 * worker E o fallback do PDF.js ("Setting up fake worker failed", que faz
 * `import()` da mesma URL) — toda miniatura/prévia caía em "Não foi possível
 * exibir o PDF aqui." e o upload em "Não foi possível ler esse PDF". Aqui o
 * worker é baixado uma vez e republicado como `blob:` com `text/javascript`,
 * então o MIME deixa de depender do servidor. Não empacotar o worker via
 * `new Worker(new URL(...))`: o rollup gera um chunk de ~1 MB (fura o teto
 * do chunk-budget) e estoura a memória do build no slot.
 *
 * `ensurePdfWorker()` resolve quando o `workerSrc` está pronto (ou quando não
 * há como melhorar: sem `Worker`/`fetch`, ou falha de rede → fica a URL do
 * asset). O `<PdfViewer>` e a checagem do upload esperam por ela antes de
 * abrir um documento, para o PDF.js nunca subir o worker pela URL crua.
 */
let pdfWorkerReady: Promise<void> | null = null;
let pdfWorkerSettled = false;

// No dev server do Vite o `.mjs` já sai com o MIME certo e o arquivo servido
// é TRANSFORMADO (ganha `import "/@vite/client"`), que não resolve a partir de
// um `blob:` — lá o worker fica na URL original.
const isViteDevServer = (import.meta as { env?: { DEV?: boolean } }).env?.DEV === true;

export function ensurePdfWorker(): Promise<void> {
  if (pdfWorkerReady) return pdfWorkerReady;
  const canUpgrade =
    !isViteDevServer &&
    typeof Worker !== 'undefined' &&
    typeof fetch !== 'undefined' &&
    typeof Blob !== 'undefined' &&
    typeof URL.createObjectURL === 'function';
  if (!canUpgrade) {
    pdfWorkerSettled = true;
    pdfWorkerReady = Promise.resolve();
    return pdfWorkerReady;
  }
  pdfWorkerReady = (async () => {
    try {
      const res = await fetch(PDF_WORKER_URL);
      if (!res.ok) return;
      const source = await res.text();
      const blob = new Blob([source], { type: 'text/javascript' });
      pdfjs.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
    } catch {
      // Fica a URL do asset: em servidores com o MIME certo o PDF.js funciona igual.
    } finally {
      pdfWorkerSettled = true;
    }
  })();
  return pdfWorkerReady;
}
// Começa cedo (em paralelo com a rota), e em ambiente sem `Worker` (testes)
// já resolve de forma síncrona.
void ensurePdfWorker();

/**
 * Teto de tempo do LOAD do documento no PDF.js. Se o `<Document>` não resolve
 * (nem sucesso, nem erro) nesse prazo — WebView antiga em que o worker trava,
 * rede ruim no meio do fetch dos bytes — a prévia troca o "Carregando o PDF…"
 * PERPÉTUO pelo fallback acionável (EU-696 / SC-004), para o candidato nunca
 * ficar sem saída no mobile.
 */
export const PDF_LOAD_TIMEOUT_MS = 15_000;

/**
 * DPR (devicePixelRatio) usado no backing-store do canvas de cada página. O
 * `react-pdf` usa `window.devicePixelRatio` por padrão (2–3 no mobile), e a
 * ÁREA do canvas cresce por dpr² — em página alta / multipágina isso pode
 * estourar o teto de canvas do iOS Safari e pintar a página em branco. Capamos
 * em 2 (sem perda visível — o desktop já é ≤ 2) para proteger o mobile. Valor
 * inválido (não-finito, ≤ 0, ausente) vira 1.
 */
export function clampDevicePixelRatio(raw: number | undefined): number {
  const dpr = typeof raw === 'number' && Number.isFinite(raw) && raw > 0 ? raw : 1;
  return Math.min(dpr, 2);
}

export interface PdfViewerProps {
  /** URL do PDF (presignada do S3, pública, ou object URL `blob:`). */
  url: string;
  /** Rótulo acessível do documento (nome do arquivo). */
  title?: string;
  /** Classes do container — passe o tamanho/borda/raio (ex.: `size-full`). */
  className?: string;
  /**
   * O que renderizar quando o PDF.js falha ao carregar (ex.: PDF sem CORS):
   *  - `button` (default): cai num botão "Abrir PDF" (abre em nova aba);
   *  - `embed`: cai num `<iframe>` nativo (embed direto, sem botão). Útil para
   *    conteúdo onde o consumidor espera ver o PDF embutido, não um link.
   *  - `none`: NÃO renderiza fallback nenhum — o consumidor trata o erro via
   *    `onLoadError`. Obrigatório em contexto com bloqueio de download
   *    (EU-594 RN-33): o botão/iframe abririam o PDF no viewer nativo, que
   *    permite salvar/imprimir.
   */
  fallback?: 'button' | 'embed' | 'none';
  /** Notifica o consumidor quando o PDF.js falha ao carregar o documento. */
  onLoadError?: () => void;
  /**
   * Chamado quando o PDF é protegido por senha (antes do `onLoadError`). O
   * viewer NÃO pede a senha — sem isso o `react-pdf` chamaria `window.prompt`,
   * que congela a aba — e trata o caso como falha de carga (cai no `fallback`).
   * Use para mostrar a mensagem certa ("PDF protegido por senha").
   */
  onPasswordRequired?: () => void;
  /**
   * `thumbnail`: miniatura dentro de uma caixa de tamanho fixo (ex.: razão A4).
   * Sem fundo cinza nem respiro em volta — a página ocupa a caixa — e um PDF de
   * 1 página é encaixado inteiro (contain), então nunca ganha barra de rolagem.
   * @default 'default' (fit-width com respiro, fundo `muted`).
   */
  variant?: 'default' | 'thumbnail';
  /**
   * Só com `variant="thumbnail"`: `contain` (padrão) encaixa um PDF de 1 página
   * inteiro na caixa; `width` usa a largura toda e rola por dentro (prévia em
   * tela cheia, onde a página é bem mais alta que a caixa).
   */
  fit?: 'contain' | 'width';
  /** Test id do container (mantém os ids `*-pdf` / `*-preview` dos consumidores). */
  'data-testid'?: string;
}

/**
 * Largura da página na miniatura (`variant="thumbnail"`). Com 1 página, encaixa
 * a página inteira na caixa (a menor entre a largura e a altura × razão), de
 * modo que o conteúdo nunca passa da caixa. Sem isso a página ocupava a largura,
 * ficava ~1 px mais alta que a caixa, a barra de rolagem aparecia, estreitava a
 * página, que passava a caber, a barra sumia… e a miniatura redesenhava sem
 * parar (QA 29/09, item 8). Com várias páginas o conteúdo sempre rola: usa a
 * largura útil (`clientWidth`, já sem a barra, que fica fixa).
 */
export function thumbnailPageWidth(box: {
  width: number;
  height: number;
  clientWidth: number;
  numPages: number;
  /** altura ÷ largura da 1ª página (`null` enquanto não carregou). */
  pageRatio: number | null;
}): number {
  if (box.numPages === 1 && box.pageRatio && box.pageRatio > 0 && box.height > 0) {
    return Math.max(0, Math.floor(Math.min(box.width, box.height / box.pageRatio)));
  }
  return Math.max(0, Math.floor(box.clientWidth));
}

/**
 * Visualizador de PDF cross-plataforma (substitui o `<iframe>` nativo, que não
 * renderiza no mobile). Renderiza todas as páginas em fit-width, com rolagem
 * vertical. Quando o documento falha ao carregar, cai no `fallback` (botão
 * "Abrir PDF" por default, ou um embed nativo via `fallback="embed"`).
 */
export function PdfViewer({
  url,
  title,
  className,
  fallback = 'button',
  onLoadError,
  onPasswordRequired,
  variant = 'default',
  fit = 'contain',
  'data-testid': testId,
}: PdfViewerProps) {
  const thumbnail = variant === 'thumbnail';
  const containerRef = useRef<HTMLDivElement>(null);
  // react-pdf precisa de uma largura em px para renderizar a página; medimos o
  // container e re-renderizamos no resize (rotação de tela, etc.).
  const [pageWidth, setPageWidth] = useState(0);
  const [numPages, setNumPages] = useState(0);
  const [errored, setErrored] = useState(false);
  const [pageRatio, setPageRatio] = useState<number | null>(null);
  // DEV-483 — só abre o documento com o worker pronto (ver `ensurePdfWorker`).
  const [workerReady, setWorkerReady] = useState(pdfWorkerSettled);

  useEffect(() => {
    if (workerReady) return;
    let active = true;
    void ensurePdfWorker().then(() => {
      if (active) setWorkerReady(true);
    });
    return () => {
      active = false;
    };
  }, [workerReady]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const measure = () => {
      const w = thumbnail
        ? thumbnailPageWidth({
            width: el.offsetWidth,
            height: el.offsetHeight,
            clientWidth: el.clientWidth,
            numPages: fit === 'width' ? Math.max(numPages, 2) : numPages,
            pageRatio,
          })
        : // `-8` deixa uma folga para a barra de rolagem não cortar a página.
          el.clientWidth - 8;
      if (w > 0) setPageWidth(w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    // Miniatura: a caixa externa (border-box) não muda quando a barra de
    // rolagem aparece — sem realimentar a medida.
    ro.observe(el, thumbnail ? { box: 'border-box' } : undefined);
    return () => ro.disconnect();
  }, [thumbnail, fit, numPages, pageRatio]);

  // EU-696 — se o LOAD do documento não resolve (nem `onLoadSuccess`, nem
  // `onLoadError`) dentro do teto, força o fallback acionável em vez do
  // "Carregando o PDF…" perpétuo (mobile com worker travado). Limpo ao
  // carregar / errar / desmontar.
  useEffect(() => {
    if (numPages > 0 || errored) return;
    const timer = setTimeout(() => setErrored(true), PDF_LOAD_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [numPages, errored]);

  return (
    <div
      ref={containerRef}
      className={cn(
        'overflow-auto',
        // Miniatura: branca e corrida, sem cinza em volta nem entre as páginas
        // (EU-762 QA 29/09: a prévia parece um documento contínuo).
        thumbnail ? 'bg-background' : 'bg-muted',
        // Encaixe pela largura: o espaço da barra de rolagem fica reservado, então
        // a largura medida não muda quando a barra aparece (sem redesenho em loop).
        thumbnail && fit === 'width' && '[scrollbar-gutter:stable]',
        className
      )}
      role="document"
      aria-label={title}
      data-testid={testId}
      // Mantém a URL legível pelo DOM (debug + testes) sem reintroduzir iframe.
      data-pdf-url={url}
    >
      {errored ? (
        <PdfFallback url={url} mode={fallback} title={title} />
      ) : !workerReady ? (
        <PdfStatus>Carregando o PDF…</PdfStatus>
      ) : (
        <Document
          file={url}
          onLoadSuccess={(doc) => setNumPages(doc.numPages)}
          onLoadError={(error) => {
            // EU-696 — o motivo do load-fail era engolido; logar para
            // diagnóstico (ex.: worker/`Promise.withResolvers` no mobile).
            console.error('[PdfViewer] Falha ao carregar o documento PDF', error);
            setErrored(true);
            onLoadError?.();
          }}
          // EU-761/762 — PDF com senha: sem `onPassword` o react-pdf chama
          // `window.prompt` (modal síncrono) e a aba congela. NÃO respondemos
          // ao callback do PDF.js (o load fica pendente e é descartado ao
          // desmontar o <Document>) e tratamos como falha de carga.
          onPassword={() => {
            setErrored(true);
            onPasswordRequired?.();
            onLoadError?.();
          }}
          loading={<PdfStatus>Carregando o PDF…</PdfStatus>}
          error={<PdfFallback url={url} mode={fallback} title={title} />}
          className={cn('flex flex-col items-center', !thumbnail && 'gap-3 py-3')}
          // EU-491: os hyperlinks do PDF abrem em nova aba, preservando a
          // navegação do usuário. `rel` evita que a aba aberta ganhe acesso ao
          // `window.opener` (reverse tabnabbing).
          externalLinkTarget="_blank"
          externalLinkRel="noopener noreferrer"
        >
          {Array.from({ length: numPages }, (_, index) => (
            <Page
              key={`pdf-page-${index + 1}`}
              pageNumber={index + 1}
              width={pageWidth > 0 ? pageWidth : undefined}
              // EU-696 — capa o DPR (2–3 no mobile) em 2 para o backing-store do
              // canvas não estourar o teto do iOS Safari (página em branco).
              devicePixelRatio={clampDevicePixelRatio(
                typeof window !== 'undefined' ? window.devicePixelRatio : undefined
              )}
              // Texto desligado (mais leve), mas a camada de ANOTAÇÃO fica
              // ligada (EU-491): é onde vivem os hyperlinks do PDF. Sem ela o
              // clique no link é ignorado. O CSS importado acima posiciona os
              // `<a>` sobre o canvas.
              renderTextLayer={false}
              renderAnnotationLayer={true}
              loading=""
              onLoadSuccess={
                thumbnail && index === 0
                  ? (page) => {
                      if (page.originalWidth > 0) {
                        setPageRatio(page.originalHeight / page.originalWidth);
                      }
                    }
                  : undefined
              }
              className={cn('max-w-full', !thumbnail && 'shadow-sm')}
            />
          ))}
        </Document>
      )}
    </div>
  );
}

function PdfStatus({ children }: { children: ReactNode }) {
  return (
    <div className="flex h-full min-h-[200px] items-center justify-center p-6 text-center text-sm text-muted-foreground">
      {children}
    </div>
  );
}

function PdfFallback({
  url,
  mode,
  title,
}: {
  url: string;
  mode: 'button' | 'embed' | 'none';
  title?: string;
}) {
  // `none`: o consumidor cuida do erro (via onLoadError) — nada de botão ou
  // iframe que entregariam o PDF ao viewer nativo (com download/print).
  if (mode === 'none') return null;
  // Embed: `<iframe>` nativo. O PDF.js falha em PDFs sem CORS (ele FETCHA os
  // bytes); o iframe apenas NAVEGA até a URL (sem CORS), então o viewer nativo
  // do browser embute o PDF no desktop — sem o botão "Abrir PDF".
  if (mode === 'embed') {
    return (
      <iframe
        src={url}
        title={title ?? 'PDF'}
        className="h-[70vh] min-h-[420px] w-full border-0"
        data-testid="pdf-viewer-embed-fallback"
      />
    );
  }
  return (
    <div className="flex h-full min-h-[200px] flex-col items-center justify-center gap-3 p-6 text-center text-muted-foreground">
      <p className="text-sm">Não foi possível exibir o PDF aqui.</p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => window.open(url, '_blank', 'noopener,noreferrer')}
        data-testid="pdf-viewer-open-fallback"
      >
        Abrir PDF
      </Button>
    </div>
  );
}
